/**
 * Prefab workflow (pure): what an instance looks like, which properties it
 * overrides, Apply / Revert (all or per property), Unpack, creating prefabs
 * and variants. Mirrors prism_core `scene.rs` + `prefab_overrides.rs`:
 *
 * - An instance is a scene entity with a `prefab` component. Its own
 *   components win over the prefab root's (whole components); root
 *   components it does not define are inherited.
 * - The prefab's other entities appear below it as `"<instance>/<id>"`
 *   (nested prefabs: `"<instance>/<nested>/<id>"`) and are changed with
 *   `prefab.overrides` entries (see {@link PrefabOverride}).
 * - A variant is a prefab whose root has a `prefab` component: the base is
 *   instantiated first, the variant's root components and overrides win.
 */

import {
  fullTransform,
  prefabInstanceOf,
  prefabRef,
  slug,
  subtreeIds,
  uniqueWith,
  upsertOverride,
  type Components,
  type EditorCommand,
  type EntityDesc,
  type PrefabOverride,
  type SceneDoc,
} from './scene-doc.js';

export { prefabInstanceOf, prefabRef, upsertOverride, type PrefabOverride };

export const PREFAB_FORMAT = 'prism.prefab/1';

export interface PrefabDoc {
  format?: string;
  name?: string;
  entities: EntityDesc[];
  [extra: string]: unknown;
}

/** Synchronous prefab lookup (the editor's prefab cache): undefined = not loaded yet, null = missing. */
export type PrefabLoader = (path: string) => PrefabDoc | null | undefined;

/** Default nesting limit (`settings.limits.maxPrefabDepth`). */
export const MAX_PREFAB_DEPTH = 8;

/* ------------------------------------------------------------------ */
/* JSON paths */

function clone<T>(value: T): T {
  return value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T);
}

const isObj = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

export function getPath(root: unknown, path: readonly string[]): unknown {
  let cur: unknown = root;
  for (const p of path) {
    if (!isObj(cur)) return undefined;
    cur = cur[p];
  }
  return cur;
}

/** Copy of `root` with `value` at `path` (`undefined` removes the leaf). */
export function setPath(root: unknown, path: readonly string[], value: unknown): unknown {
  if (path.length === 0) return value === undefined ? root : clone(value);
  const [head, ...rest] = path as [string, ...string[]];
  const base: Record<string, unknown> = isObj(root) ? { ...root } : {};
  if (rest.length === 0) {
    if (value === undefined) delete base[head];
    else base[head] = clone(value);
    return base;
  }
  if (value === undefined && !isObj(base[head])) return base;
  base[head] = setPath(base[head], rest, value);
  return base;
}

const split = (path: string | undefined) => (path ?? '').split('.').filter(Boolean);

/** Applies one override to an entity description (null = the entity is removed). */
export function applyOverride(desc: EntityDesc, o: PrefabOverride): EntityDesc | null {
  const parts = split(o.path);
  const value = o.removed ? undefined : o.value;
  if (parts.length === 0) return o.removed ? null : desc;
  const [head, ...rest] = parts as [string, ...string[]];
  if (head === 'name') return { ...desc, name: typeof value === 'string' ? value : null };
  if (head === 'transform') {
    const t = rest.length === 0 ? (isObj(value) ? value : {}) : setPath(fullTransform(desc.transform), rest, value);
    return { ...desc, transform: t as EntityDesc['transform'] };
  }
  if (head === 'components' && rest.length > 0) {
    const components = setPath(desc.components ?? {}, rest, value === null ? undefined : value) as Components;
    return { ...desc, components };
  }
  return desc;
}

/* ------------------------------------------------------------------ */
/* Expansion */

/** An entity of an expanded instance. */
export interface ExpandedEntity {
  /** Live id (`"<instance>/<target>"`) and effective description (parent = live parent id). */
  entity: EntityDesc;
  /** Scene entity of the outermost instance. */
  instance: string;
  /** Id relative to that instance. */
  target: string;
  /** The root of a nested prefab instance (has its own `prefab` component). */
  nested: boolean;
}

export interface ExpandedInstance {
  /** The instance root as it plays: own components + inherited root components. */
  root: EntityDesc;
  /** Root component keys inherited from the prefab (not the instance's own). */
  inherited: string[];
  children: ExpandedEntity[];
  /** Prefabs that could not be read (or are not loaded yet). */
  missing: string[];
  /** Cycles / nesting limit problems. */
  problems: string[];
  /** Some prefab is still loading. */
  pending: boolean;
}

interface Ctx {
  load: PrefabLoader;
  out: ExpandedInstance;
  instance: string;
  maxDepth: number;
}

/**
 * Expands a prefab instance (a scene entity with a `prefab` component):
 * effective root, every prefab entity below it with overrides applied.
 */
export function expandInstance(host: EntityDesc, load: PrefabLoader, options: { maxDepth?: number; withOverrides?: boolean } = {}): ExpandedInstance {
  const out: ExpandedInstance = { root: clone(host), inherited: [], children: [], missing: [], problems: [], pending: false };
  const ref = prefabRef(host);
  if (!ref) return out;
  const ctx: Ctx = { load, out, instance: host.id, maxDepth: options.maxDepth ?? MAX_PREFAB_DEPTH };
  const effective = options.withOverrides === false ? { ...ref, overrides: [] } : ref;
  expandRef(ctx, host.id, effective, [], (keys) => out.inherited.push(...keys));
  return out;
}

/** Spawns prefab `ref` under live entity `hostId` (mirrors instantiate_prefab_ref). */
function expandRef(
  ctx: Ctx,
  hostId: string,
  ref: { path: string; overrides?: PrefabOverride[] },
  stack: string[],
  onInherit: (keys: string[]) => void,
): void {
  const { out } = ctx;
  if (stack.includes(ref.path)) {
    out.problems.push(`'${hostId}': prefab '${ref.path}' includes itself (${[...stack, ref.path].join(' → ')})`);
    return;
  }
  if (stack.length >= ctx.maxDepth) {
    out.problems.push(`'${hostId}': prefab '${ref.path}' is nested deeper than ${ctx.maxDepth}`);
    return;
  }
  const doc = ctx.load(ref.path);
  if (doc === undefined) {
    out.pending = true;
    return;
  }
  if (!doc || doc.entities.length === 0) {
    out.missing.push(ref.path);
    return;
  }
  const next = [...stack, ref.path];
  const [root, ...rest] = doc.entities as [EntityDesc, ...EntityDesc[]];
  // 1. Root components the host does not define.
  const host = findLive(ctx, hostId);
  if (host) {
    const own = host.components ?? {};
    const added: string[] = [];
    const merged: Components = { ...own };
    for (const [k, v] of Object.entries(root.components ?? {})) {
      if (k === 'prefab' || k in own) continue;
      merged[k] = clone(v);
      added.push(k);
    }
    setLive(ctx, hostId, { ...host, components: merged });
    onInherit(added);
  }
  const rootRef = prefabRef(root);
  if (rootRef) expandRef(ctx, hostId, rootRef, next, onInherit);
  // 2. The other entities below the host.
  const local = new Map<string, string>([[root.id, hostId]]);
  for (const d of rest) local.set(d.id, `${hostId}/${d.id}`);
  const spawned: { id: string; desc: EntityDesc }[] = [];
  for (const d of rest) {
    const id = `${hostId}/${d.id}`;
    const parent = d.parent ? (local.get(d.parent) ?? hostId) : hostId;
    const entity: EntityDesc = { ...clone(d), id, parent };
    const target = id.slice(ctx.instance.length + 1);
    out.children.push({ entity, instance: ctx.instance, target, nested: prefabRef(d) !== null });
    spawned.push({ id, desc: d });
  }
  for (const { id, desc } of spawned) {
    const nested = prefabRef(desc);
    if (nested) expandRef(ctx, id, nested, next, () => {});
  }
  // 3. The instance's overrides.
  for (const o of ref.overrides ?? []) {
    const id = o.target ? `${hostId}/${o.target}` : hostId;
    const current = findLive(ctx, id);
    if (!current) {
      out.problems.push(`'${hostId}': override target '${o.target}' not found in ${ref.path}`);
      continue;
    }
    const changed = applyOverride(current, o);
    if (changed) setLive(ctx, id, changed);
    else removeLive(ctx, id);
  }
}

function findLive(ctx: Ctx, id: string): EntityDesc | null {
  if (id === ctx.instance) return ctx.out.root;
  return ctx.out.children.find((c) => c.entity.id === id)?.entity ?? null;
}

function setLive(ctx: Ctx, id: string, desc: EntityDesc): void {
  if (id === ctx.instance) {
    ctx.out.root = desc;
    return;
  }
  const c = ctx.out.children.find((x) => x.entity.id === id);
  if (c) c.entity = { ...desc, id, parent: c.entity.parent };
}

function removeLive(ctx: Ctx, id: string): void {
  const gone = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const c of ctx.out.children) {
      if (!gone.has(c.entity.id) && c.entity.parent && gone.has(c.entity.parent)) {
        gone.add(c.entity.id);
        grew = true;
      }
    }
  }
  ctx.out.children = ctx.out.children.filter((c) => !gone.has(c.entity.id));
}

/* ------------------------------------------------------------------ */
/* The scene with its instances expanded (hierarchy, inspector) */

export type PrefabStatus = 'instance' | 'overridden' | 'missing' | 'child' | 'nested' | 'childOverridden' | 'added';

export interface ExpandedScene {
  /** Scene entities and prefab entities, parents before children (document order). */
  entities: EntityDesc[];
  /** Prefab state by entity id (absent = plain scene entity). */
  status: Map<string, PrefabStatus>;
  /** Expansion of each instance by scene entity id. */
  instances: Map<string, ExpandedInstance>;
  /** Some prefab is still loading. */
  pending: boolean;
}

/** Every prefab instance of the scene expanded (prefab entities follow their instance). */
export function expandScene(doc: SceneDoc, load: PrefabLoader): ExpandedScene {
  const status = new Map<string, PrefabStatus>();
  const instances = new Map<string, ExpandedInstance>();
  const entities: EntityDesc[] = [];
  let pending = false;
  for (const e of doc.entities) {
    const ref = prefabRef(e);
    if (!ref) {
      entities.push(e);
      continue;
    }
    const x = expandInstance(e, load);
    instances.set(e.id, x);
    pending ||= x.pending;
    entities.push(e);
    const own = instanceOverrides(e, x, load);
    status.set(e.id, x.missing.length > 0 ? 'missing' : own.length > 0 ? 'overridden' : 'instance');
    const touched = new Set((ref.overrides ?? []).map((o) => (o.target ? `${e.id}/${o.target}` : e.id)));
    for (const c of x.children) {
      entities.push(c.entity);
      status.set(c.entity.id, c.nested ? 'nested' : touched.has(c.entity.id) ? 'childOverridden' : 'child');
    }
  }
  // Scene entities added under a prefab entity.
  for (const e of doc.entities) {
    if (e.parent && !doc.entities.some((x) => x.id === e.parent) && prefabInstanceOf(doc, e.parent)) status.set(e.id, 'added');
  }
  return { entities, status, instances, pending };
}

/* ------------------------------------------------------------------ */
/* Overrides of one instance */

/** One overridden property, for the Overrides list / per-property Apply & Revert. */
export interface OverrideItem {
  /** Live entity id (`"<instance>"` or `"<instance>/<target>"`). */
  id: string;
  target: string;
  /** Dotted path (`components.light.intensity`, `name`, `transform.position`, `""` = removed entity). */
  path: string;
  /** Root overrides are the instance's own components (not `prefab.overrides` entries). */
  root: boolean;
  removed: boolean;
  label: string;
}

/**
 * Everything an instance overrides: its own root components that differ from
 * the prefab root (per field when both are objects) and its override entries.
 */
export function instanceOverrides(host: EntityDesc, expanded: ExpandedInstance | null, load: PrefabLoader): OverrideItem[] {
  const ref = prefabRef(host);
  if (!ref) return [];
  const items: OverrideItem[] = [];
  const base = expandInstance({ id: host.id, components: { prefab: { path: ref.path } } }, load, { withOverrides: false });
  const baseRoot = base.root.components ?? {};
  for (const [key, value] of Object.entries(host.components ?? {})) {
    if (key === 'prefab' || !(key in baseRoot)) continue;
    const b = baseRoot[key];
    if (isObj(b) && isObj(value)) {
      for (const field of new Set([...Object.keys(b), ...Object.keys(value)])) {
        if (!sameValue(b[field], value[field])) {
          items.push({ id: host.id, target: '', path: `components.${key}.${field}`, root: true, removed: false, label: `${key}.${field}` });
        }
      }
    } else if (!sameValue(b, value)) {
      items.push({ id: host.id, target: '', path: `components.${key}`, root: true, removed: false, label: key });
    }
  }
  for (const o of ref.overrides ?? []) {
    const path = o.path ?? '';
    const id = o.target ? `${host.id}/${o.target}` : host.id;
    const name = expanded?.children.find((c) => c.entity.id === id)?.entity.name ?? o.target;
    const what = path === '' ? 'removed' : o.removed ? `${path.replace(/^components\./, '')} removed` : path.replace(/^components\./, '');
    items.push({ id, target: o.target, path, root: false, removed: Boolean(o.removed), label: `${name || '(root)'} · ${what}` });
  }
  return items;
}

/** Is the property at `path` of live entity `id` overridden? (Inspector field marks.) */
export function isOverridden(items: readonly OverrideItem[], id: string, path: string): boolean {
  return items.some((i) => i.id === id && (i.path === path || path.startsWith(`${i.path}.`) || i.path.startsWith(`${path}.`)));
}

/** Value of `path` on the prefab (no overrides) for live entity `id`. */
export function baseValue(host: EntityDesc, id: string, path: string, load: PrefabLoader): unknown {
  const ref = prefabRef(host);
  if (!ref) return undefined;
  const base = expandInstance({ id: host.id, components: { prefab: { path: ref.path } } }, load, { withOverrides: false });
  const desc = id === host.id ? base.root : base.children.find((c) => c.entity.id === id)?.entity;
  if (!desc) return undefined;
  const parts = split(path);
  if (parts[0] === 'transform') return getPath(fullTransform(desc.transform), parts.slice(1));
  return getPath(desc, parts);
}

const sameValue = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/* ------------------------------------------------------------------ */
/* Revert */

/** Commands reverting overrides of an instance (all when `items` is omitted). One batch. */
export function revertCommands(host: EntityDesc, items: readonly OverrideItem[] | null, load: PrefabLoader): EditorCommand[] {
  const ref = prefabRef(host);
  if (!ref) return [];
  const all = items ?? instanceOverrides(host, null, load);
  let components: Components = { ...(host.components ?? {}) };
  let overrides = [...(ref.overrides ?? [])];
  const base = expandInstance({ id: host.id, components: { prefab: { path: ref.path } } }, load, { withOverrides: false }).root.components ?? {};
  for (const item of all) {
    if (item.root) {
      const parts = split(item.path); // components.<key>[.<field>]
      const key = parts[1]!;
      if (parts.length === 2) delete components[key];
      else {
        const next = setPath(components[key], parts.slice(2), getPath(base[key], parts.slice(2)));
        if (sameValue(next, base[key])) delete components[key];
        else components = { ...components, [key]: next };
      }
    } else {
      overrides = overrides.filter((o) => !(o.target === item.target && (o.path ?? '') === item.path));
    }
  }
  components.prefab = overrides.length > 0 ? { ...ref, overrides } : stripOverrides(ref);
  return componentDiff(host, components);
}

function stripOverrides(ref: { path: string; overrides?: PrefabOverride[] }): Record<string, unknown> {
  const { overrides: _drop, ...rest } = ref as Record<string, unknown> & { overrides?: unknown };
  void _drop;
  return rest;
}

/** setComponent / removeComponent commands turning `host.components` into `next`. */
function componentDiff(host: EntityDesc, next: Components): EditorCommand[] {
  const out: EditorCommand[] = [];
  const before = host.components ?? {};
  for (const key of new Set([...Object.keys(before), ...Object.keys(next)])) {
    if (!(key in next)) out.push({ cmd: 'removeComponent', id: host.id, key });
    else if (!sameValue(before[key], next[key])) out.push({ cmd: 'setComponent', id: host.id, key, value: next[key] });
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Apply */

/** Writes an override into a prefab document (targets relative to an instance of it). */
export function applyOverrideToPrefab(prefab: PrefabDoc, o: PrefabOverride): PrefabDoc {
  const entities = prefab.entities.slice();
  const root = entities[0];
  if (!root) return prefab;
  if (!o.target) {
    const changed = applyOverride(root, { ...o, target: '' });
    if (changed) entities[0] = changed;
    return { ...prefab, entities };
  }
  const [first, ...rest] = o.target.split('/') as [string, ...string[]];
  const i = entities.findIndex((e, n) => n > 0 && e.id === first);
  if (i > 0 && rest.length === 0) {
    const changed = applyOverride(entities[i]!, o);
    if (changed) entities[i] = changed;
    else {
      const gone = new Set(subtreeIds({ entities }, first));
      return { ...prefab, entities: entities.filter((e) => !gone.has(e.id)) };
    }
    return { ...prefab, entities };
  }
  // Inside a nested prefab (or an entity of a variant's base): an override one level down.
  const holder = i > 0 && rest.length > 0 && prefabRef(entities[i]) ? i : prefabRef(root) ? 0 : -1;
  if (holder < 0) return prefab;
  const ref = prefabRef(entities[holder])!;
  const target = holder === 0 ? o.target : rest.join('/');
  const overrides = upsertOverride(ref.overrides ?? [], { ...o, target });
  entities[holder] = { ...entities[holder]!, components: { ...entities[holder]!.components, prefab: { ...ref, overrides } } };
  return { ...prefab, entities };
}

/**
 * Apply overrides to the prefab asset: the new prefab document plus the
 * scene commands that drop the applied overrides from the instance.
 */
export function applyToPrefab(
  host: EntityDesc,
  prefab: PrefabDoc,
  items: readonly OverrideItem[] | null,
  load: PrefabLoader,
): { prefab: PrefabDoc; commands: EditorCommand[] } {
  const ref = prefabRef(host);
  if (!ref) return { prefab, commands: [] };
  const all = items ?? instanceOverrides(host, null, load);
  let next = prefab;
  for (const item of all) {
    if (item.root) {
      const value = getPath(host.components ?? {}, split(item.path).slice(1));
      next = applyOverrideToPrefab(next, { target: '', path: item.path, value });
    } else {
      const o = (ref.overrides ?? []).find((x) => x.target === item.target && (x.path ?? '') === item.path);
      if (o) next = applyOverrideToPrefab(next, o);
    }
  }
  // The instance now matches the prefab for those properties: revert them.
  const loadNext: PrefabLoader = (p) => (p === ref.path ? next : load(p));
  return { prefab: next, commands: revertCommands(host, all, loadNext) };
}

/* ------------------------------------------------------------------ */
/* Unpack */

/**
 * Unpack an instance into plain scene entities. `completely` also unpacks
 * nested instances; otherwise they stay prefab instances (keeping the
 * overrides that targeted them).
 */
export function unpackCommands(doc: SceneDoc, hostId: string, load: PrefabLoader, completely = false): EditorCommand[] {
  const host = doc.entities.find((e) => e.id === hostId);
  const ref = prefabRef(host);
  if (!host || !ref) return [];
  const x = expandInstance(host, load);
  if (x.pending || x.missing.length > 0) return [];
  const taken = new Set(doc.entities.map((e) => e.id));
  const idMap = new Map<string, string>([[hostId, hostId]]);
  // Nested instances kept as prefabs (one level): their subtrees are not unpacked.
  const keepNested = completely ? [] : x.children.filter((c) => c.nested).map((c) => c.entity.id);
  const insideKept = (id: string) => keepNested.some((n) => id.startsWith(`${n}/`));
  const commands: EditorCommand[] = [];
  const rootComponents: Components = { ...(x.root.components ?? {}) };
  delete rootComponents.prefab;
  commands.push(...componentDiff(host, rootComponents));
  if (x.root.name && x.root.name !== host.name) commands.push({ cmd: 'renameEntity', id: hostId, name: x.root.name });
  for (const c of x.children) {
    if (insideKept(c.entity.id)) continue;
    const base = slug(c.entity.id.split('/').slice(1).join('_') || c.entity.id);
    const id = uniqueWith(base, (cand) => taken.has(cand));
    taken.add(id);
    idMap.set(c.entity.id, id);
    const components: Components = { ...(c.entity.components ?? {}) };
    if (completely) delete components.prefab;
    else if (keepNested.includes(c.entity.id)) {
      // Overrides that targeted the nested instance from outside move onto it.
      const prefix = `${c.target}/`;
      const own = prefabRef(c.entity)!;
      const moved = (ref.overrides ?? []).filter((o) => o.target.startsWith(prefix)).map((o) => ({ ...o, target: o.target.slice(prefix.length) }));
      let overrides = own.overrides ?? [];
      for (const o of moved) overrides = upsertOverride(overrides, o);
      components.prefab = overrides.length ? { ...own, overrides } : stripOverrides(own);
    }
    if (completely && prefabRef(c.entity)) {
      // A nested root's inherited components are already merged into c.entity.
    }
    commands.push({
      cmd: 'addEntity',
      desc: { id, name: c.entity.name ?? null, parent: idMap.get(c.entity.parent ?? hostId) ?? hostId, transform: c.entity.transform, components },
    });
  }
  // Scene entities that were added under prefab entities.
  for (const e of doc.entities) {
    if (e.parent && idMap.has(e.parent) && e.parent !== hostId && !doc.entities.some((d) => d.id === e.parent)) {
      commands.push({ cmd: 'reparent', id: e.id, parent: idMap.get(e.parent)! });
    }
  }
  return commands;
}

/* ------------------------------------------------------------------ */
/* Create */

/** File name for a new prefab made from an entity. */
export function prefabPathFor(folder: string, name: string, exists: (path: string) => boolean): string {
  const dir = folder.replace(/\\/g, '/').replace(/\/+$/, '');
  const base = (name.trim() || 'Prefab').replace(/[^\w\- ]+/g, '').trim() || 'Prefab';
  const make = (n: string) => `${dir ? `${dir}/` : ''}${n}.prefab.json`;
  let candidate = make(base);
  for (let i = 1; exists(candidate); i += 1) candidate = make(`${base} ${i}`);
  return candidate;
}

/**
 * Prefab document from an entity and its descendants (the root keeps its
 * id, loses its parent and is placed at the origin), and the scene commands
 * replacing the subtree with an instance of `path` (same id and transform).
 */
export function createPrefabFromEntity(doc: SceneDoc, id: string, path: string): { prefab: PrefabDoc; commands: EditorCommand[] } | null {
  const root = doc.entities.find((e) => e.id === id);
  if (!root) return null;
  const ids = subtreeIds(doc, id);
  const entities = ids.map((eid) => clone(doc.entities.find((e) => e.id === eid)!));
  const t = fullTransform(entities[0]!.transform);
  entities[0] = { ...entities[0]!, parent: null, transform: { position: [0, 0, 0], rotation: [0, 0, 0], scale: t.scale } };
  for (const e of entities.slice(1)) if (!e.parent || !ids.includes(e.parent)) e.parent = id;
  const prefab: PrefabDoc = { format: PREFAB_FORMAT, name: root.name || id, entities };
  const commands: EditorCommand[] = [];
  for (const child of doc.entities.filter((e) => e.parent === id)) commands.push({ cmd: 'deleteEntity', id: child.id });
  for (const key of Object.keys(root.components ?? {})) commands.push({ cmd: 'removeComponent', id, key });
  commands.push({ cmd: 'setComponent', id, key: 'prefab', value: { path } });
  return { prefab, commands };
}

/** A new variant of prefab `basePath`. */
export function createVariant(basePath: string, base: PrefabDoc | null): PrefabDoc {
  const name = `${base?.name || basePath.replace(/^.*\//, '').replace(/\.prefab\.json$/, '')} Variant`;
  const rootId = base?.entities[0]?.id ?? 'root';
  return { format: PREFAB_FORMAT, name, entities: [{ id: rootId, name, components: { prefab: { path: basePath } } }] };
}

/** Base prefab of a variant (null for an ordinary prefab). */
export function variantBase(prefab: PrefabDoc | null | undefined): string | null {
  return prefabRef(prefab?.entities[0])?.path ?? null;
}

/** Prefab paths an instance depends on (its prefab, variant bases, nested prefabs). */
export function prefabDependencies(path: string, load: PrefabLoader, seen = new Set<string>()): string[] {
  if (seen.has(path)) return [];
  seen.add(path);
  const doc = load(path);
  if (!doc) return [path];
  const out = [path];
  for (const e of doc.entities) {
    const r = prefabRef(e);
    if (r) out.push(...prefabDependencies(r.path, load, seen));
  }
  return out;
}

export function serializePrefab(doc: PrefabDoc): string {
  return `${JSON.stringify(doc, null, 2)}\n`;
}
